import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import type { PostSummary } from '../../types'
import { TagBadge } from './TagBadge'
import { PostMeta } from './PostMeta'

interface PostCardProps {
  post: PostSummary
  featured?: boolean
}

export const PostCard: React.FC<PostCardProps> = ({ post, featured = false }) => {
  const [hovered, setHovered] = useState(false)

  return (
    <>
      <style>{`
        .post-card {
          background-color: var(--card-bg);
          border: 0.5px solid var(--card-border);
          border-radius: var(--radius-lg, 10px);
          padding: 22px 26px;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          display: flex;
          flex-direction: column;
          gap: 10px;
          position: relative;
        }
        .post-card.post-card-hovered {
          border-color: var(--card-hover-border);
          box-shadow: var(--shadow-glow);
        }
        .post-card-featured-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          color: #1a1a18;
          background-color: var(--accent-amber);
          padding: 2px 8px;
          border-radius: var(--radius-sm, 4px);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          align-self: flex-start;
        }
        .post-card-title {
          font-size: 1.05rem;
          font-weight: 700;
          line-height: 1.3;
          margin: 0;
        }
        .post-card-title a {
          color: var(--text-primary);
          text-decoration: none;
          transition: color 0.15s ease;
        }
        .post-card-title a:hover {
          color: var(--accent-blue);
          text-decoration: none;
        }
        .post-card-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .post-card-summary {
          font-size: 0.875rem;
          color: var(--text-secondary);
          line-height: 1.65;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          margin: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
        }
      `}</style>
      <div
        className={`post-card${hovered ? ' post-card-hovered' : ''}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {(featured || post.featured) && (
          <span className="post-card-featured-badge">★ FEATURED</span>
        )}
        <h2 className="post-card-title">
          <Link to={`/posts/${post.slug}`}>{post.title}</Link>
        </h2>
        {post.tags.length > 0 && (
          <div className="post-card-tags">
            {post.tags.map((tag) => (
              <TagBadge key={tag} tag={tag} size="sm" />
            ))}
          </div>
        )}
        <p className="post-card-summary">{post.summary}</p>
        <PostMeta
          author={post.author}
          publishedAt={post.publishedAt}
          readingTimeMin={post.readingTimeMin}
          category={post.category}
          updatedAt={post.updatedAt}
        />
      </div>
    </>
  )
}
